import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** Supported OAuth providers */
type OAuthProvider = 'google' | 'github' | 'auth0';

/** Mock provider authorization endpoints */
const PROVIDER_AUTH_URLS: Record<OAuthProvider, string> = {
  google: 'https://accounts.google.com/o/oauth2/v2/auth',
  github: 'https://github.com/login/oauth/authorize',
  auth0: 'https://auth.example.com/authorize',
};

/** Stored token set */
interface TokenSet {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/** Mock user profile */
interface UserInfo {
  sub: string;
  email: string;
  name: string;
  picture: string;
}

/**
 * OAuth2/OIDC integration with in-memory mock backend.
 *
 * This is a development/mock implementation. No actual OAuth library is used.
 * State tokens, access tokens, and refresh tokens are generated in-memory and
 * tracked via Maps. Useful for testing OAuth flows without external providers.
 */
export class OAuthIntegration extends BaseIntegration {
  /** Maps state token -> provider for pending authorization flows */
  private states: Map<string, OAuthProvider> = new Map();

  /** Maps access token -> token set */
  private tokens: Map<string, TokenSet> = new Map();

  /** Maps refresh token -> access token for refresh lookups */
  private refreshIndex: Map<string, string> = new Map();

  /** Maps access token -> mock user session */
  private sessions: Map<string, UserInfo> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);
    this.logger.info('OAuth integration initialized (mock backend)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'authorize':
          data = await this.executeWithRetry(() => this.authorize(params));
          break;
        case 'token':
          data = await this.executeWithRetry(() => this.token(params));
          break;
        case 'refresh':
          data = await this.executeWithRetry(() => this.refresh(params));
          break;
        case 'revoke':
          data = await this.executeWithRetry(() => this.revoke(params));
          break;
        case 'userinfo':
          data = await this.executeWithRetry(() => this.userinfo(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Generate a random hex token of the given byte length. */
  private generateToken(bytes: number = 32): string {
    const chars = 'abcdef0123456789';
    let result = '';
    for (let i = 0; i < bytes * 2; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  /** Generate a mock user profile from a provider and access token. */
  private generateMockUser(provider: OAuthProvider, sub: string): UserInfo {
    return {
      sub,
      email: `user-${sub.slice(0, 8)}@${provider}.example.com`,
      name: `Mock User (${provider})`,
      picture: `https://${provider}.example.com/avatar/${sub.slice(0, 8)}.png`,
    };
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async authorize(
    params: IntegrationParams,
  ): Promise<{ authUrl: string; state: string }> {
    const provider = params.provider as OAuthProvider;
    const scopes = params.scopes as string[];
    const redirectUri = params.redirectUri as string;

    this.logger.debug('OAuth AUTHORIZE', { provider, scopes: scopes.join(', '), redirectUri });

    const state = this.generateToken(16);
    this.states.set(state, provider);

    const baseUrl = PROVIDER_AUTH_URLS[provider];
    const queryParams = new URLSearchParams({
      response_type: 'code',
      scope: scopes.join(' '),
      redirect_uri: redirectUri,
      state,
      client_id: this.config.env.CLIENT_ID ?? 'mock-client-id',
    });

    const authUrl = `${baseUrl}?${queryParams.toString()}`;

    return { authUrl, state };
  }

  private async token(
    params: IntegrationParams,
  ): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    tokenType: 'bearer';
  }> {
    const code = params.code as string;
    const state = params.state as string;

    this.logger.debug('OAuth TOKEN', { code, state });

    const provider = this.states.get(state);
    if (!provider) {
      throw new Error(
        `Invalid or expired state token: ${state}`,
      );
    }

    // Consume the state (one-time use)
    this.states.delete(state);

    const accessToken = this.generateToken(32);
    const refreshToken = this.generateToken(32);
    const expiresIn = 3600;

    const tokenSet: TokenSet = {
      accessToken,
      refreshToken,
      expiresAt: Date.now() + expiresIn * 1000,
    };

    this.tokens.set(accessToken, tokenSet);
    this.refreshIndex.set(refreshToken, accessToken);

    // Generate and store a mock user session
    const sub = this.generateToken(8);
    this.sessions.set(accessToken, this.generateMockUser(provider, sub));

    return { accessToken, refreshToken, expiresIn, tokenType: 'bearer' };
  }

  private async refresh(
    params: IntegrationParams,
  ): Promise<{ accessToken: string; expiresIn: number }> {
    const refreshToken = params.refreshToken as string;

    this.logger.debug('OAuth REFRESH', { refreshToken: refreshToken.slice(0, 8) + '...' });

    const oldAccessToken = this.refreshIndex.get(refreshToken);
    if (!oldAccessToken) {
      throw new Error('Invalid refresh token');
    }

    // Carry over user session to the new access token
    const userInfo = this.sessions.get(oldAccessToken);

    // Clean up old access token
    this.tokens.delete(oldAccessToken);
    this.sessions.delete(oldAccessToken);

    // Issue new access token
    const newAccessToken = this.generateToken(32);
    const expiresIn = 3600;

    const tokenSet: TokenSet = {
      accessToken: newAccessToken,
      refreshToken,
      expiresAt: Date.now() + expiresIn * 1000,
    };

    this.tokens.set(newAccessToken, tokenSet);
    this.refreshIndex.set(refreshToken, newAccessToken);

    if (userInfo) {
      this.sessions.set(newAccessToken, userInfo);
    }

    return { accessToken: newAccessToken, expiresIn };
  }

  private async revoke(
    params: IntegrationParams,
  ): Promise<{ revoked: boolean }> {
    const token = params.token as string;

    this.logger.debug('OAuth REVOKE', { token: token.slice(0, 8) + '...' });

    // Check if it is an access token
    const tokenSet = this.tokens.get(token);
    if (tokenSet) {
      this.refreshIndex.delete(tokenSet.refreshToken);
      this.tokens.delete(token);
      this.sessions.delete(token);
      return { revoked: true };
    }

    // Check if it is a refresh token
    const accessToken = this.refreshIndex.get(token);
    if (accessToken) {
      this.tokens.delete(accessToken);
      this.sessions.delete(accessToken);
      this.refreshIndex.delete(token);
      return { revoked: true };
    }

    return { revoked: false };
  }

  private async userinfo(
    params: IntegrationParams,
  ): Promise<UserInfo> {
    const accessToken = params.accessToken as string;

    this.logger.debug('OAuth USERINFO', { accessToken: accessToken.slice(0, 8) + '...' });

    const tokenSet = this.tokens.get(accessToken);
    if (!tokenSet) {
      throw new Error('Invalid access token');
    }

    if (Date.now() > tokenSet.expiresAt) {
      this.tokens.delete(accessToken);
      this.sessions.delete(accessToken);
      throw new Error('Access token expired');
    }

    const userInfo = this.sessions.get(accessToken);
    if (!userInfo) {
      throw new Error('No session found for access token');
    }

    return userInfo;
  }
}

// Auto-register
registerIntegration('oauth', OAuthIntegration);
